import { PropsWithChildren } from "react";
import {
    CombinationNode,
    EdgeData,
    GroupNodeType,
    MomentLocationTypes,
    MultiResponseBlockData,
    MultipleResponseNode,
    SingleResponseNode,
    ValidateInfoBlockFormType,
} from "../../types/bert";
import {
    BuildingBlockType,
    MultiResponseNodeType,
    SingleResponseBlockType,
} from "../context/NodeStateContext";
import AttributeSvg from "../svg/AttributeSvg";
import MomentSvg from "../svg/MomentSvg";
import ParameterSvg from "../svg/ParameterSvg";
import PredefinedFuncSvg from "../svg/PredefinedFuncSvg";

import {
    Node,
    NodeTypes,
    Edge,
    Viewport,
    OnNodesChange,
    OnEdgesChange,
    EdgeTypes,
    Position,
} from "reactflow";
import CreateSingleResponseNode from "../components/SingleResponse/CreateSingleResponseNode";
import CreateMultipleResponseNode from "../components/MultipleResponse/CreateMultipleResponseNode";
import SingleBlockNode from "../components/SingleResponse/SingleBlockNode";
import MultiResponseNode from "../components/MultipleResponse/MultiResponseNode";
import GroupBlockNode from "../components/SingleResponse/GroupBlockNode";
import LogicOperatorEdge from "../components/Edges/LogicOperatorEdge";
import CustomDeleteEdge from "../components/Edges/CustomDeleteEdge";
import ValidateInfoSvg from "../svg/ValidateInfoSvg";
import CombinationBlockNode from "../components/SingleResponse/CombinationBlockNode";

export interface IReactFlowStateContext {
    nodes: Node[];
    edges: Edge[];
    defaultViewport: Viewport;
    deleteNode: (id: string) => void;
    updateEdge: (e: { id: string; data: EdgeData }) => void;
    addEdge: (e: {
        target: string;
        source: string;
        block: string;
        direction?: Position;
    }) => void;
    onNodesChange: OnNodesChange;
    onEdgesChange: OnEdgesChange;
    setNodes: React.Dispatch<
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        React.SetStateAction<Node<any, string | undefined>[]>
    >;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setEdges: React.Dispatch<React.SetStateAction<Edge<any>[]>>;
    createSingleResponseNode: <
        T extends
            | SingleResponseNode<NonNullable<SingleResponseBlockType>>
            | GroupNodeType
            | CombinationNode
    >(e: {
        data: T;
        sourceId?: string;
        direction?: Position;
        blockParentId?: string;
    }) => string;
    updateSingleResponseNode: <
        T extends SingleResponseNode<
            NonNullable<SingleResponseBlockType>
        >["metadata"]
    >(e: {
        data: T;
        sourceId?: string;
        blockParentId?: string;
    }) => void;
    createMultipleResponseNode: <
        T extends MultipleResponseNode<MultiResponseNodeType>
    >(e: {
        data: T;
        sourceId?: string;
        blockId?: string;
        direction?: Position;
    }) => string;
    updateMultipleResponseNode: (e: {
        data: MultiResponseBlockData<MultiResponseNodeType>;
        sourceId: string;
    }) => void;
    isGroupBlockTree?: boolean;
    isBlockSource: (e: string) => boolean;
    isSaving: boolean;
    handleSetIsSaving: () => void;
}

export const enum BertNodeKeys {
    createSingleResponseNode = "create-single-response-node",
    createMultiResponseNode = "create-multiple-response-node",
    singleBlockNode = "single-block-node",
    groupBlockNode = "group-block-node",
    combinationBlockNode = "default-block-node",
    conditionalNode = "conditional-node",
    conditionalGptNode = "conditional-gpt-node",
    responseNode = "response-node",
}

export const enum BertEdgeKeys {
    logicOperatorEdge = "logic-edge",
    customDeleteEdge = "delete-edge",
}

export type BlockType<
    T extends
        | SingleResponseBlockType
        | BuildingBlockType
        | MultiResponseNodeType
> = {
    name: T;
    description: string;
    displayName: string;
    svgIcon: React.ReactElement;
};

export const SingleBlockTypes: Array<BlockType<SingleResponseBlockType>> = [
    {
        name: "attribute",
        displayName: "Attribute",
        description: "Tags, Types & Conv. type",
        svgIcon: <AttributeSvg />,
    },
    {
        name: "moment",
        displayName: "Moment",
        description: " Configure your Phrases",
        svgIcon: <MomentSvg />,
    },
    {
        name: "parameter",
        displayName: "Parameter",
        description: "Existing Parameter logic",
        svgIcon: <ParameterSvg />,
    },
    {
        name: "function",
        displayName: "Predefined functions",
        description: "Choose Advanced rules",
        svgIcon: <PredefinedFuncSvg />,
    },
    {
        name: "validateInfo",
        displayName: "Validate Information",
        description: "Checking Info correctness",
        svgIcon: <ValidateInfoSvg />,
    },
];

export type OptionTypeId = `${MomentLocationTypes}.${"-" | "+" | "+/-"}`;

export const momentLocationOptions: Array<{
    id: OptionTypeId;
    label: string;
}> = [
    {
        id: "recording_start_time.+",
        label: "From Recording Start Time",
    },
    {
        id: "recording_end_time.-",
        label: "Before Recording End Time",
    },
    {
        id: "talk_start_time.+",
        label: "From Transcript Start Time",
    },
    {
        id: "talk_end_time.-",
        label: "Before Transcript End Time",
    },
    {
        id: "moment_id.+",
        label: "From Moment",
    },
    {
        id: "moment_id.-",
        label: "Before Moment",
    },
    {
        id: "moment_id.+/-",
        label: "Nearby Moment",
    },
];

export const attribute_types = [
    { id: "conversation_type", label: "Conversation Type" },
    { id: "type", label: "Type" },
    { id: "tags", label: "tags" },
    { id: "entity", label: "Entity" },
    { id: "ai_disposition", label: "AI Disposition" },
];

export const predefinedFunctionOptions = [
    {
        id: "dead_air",
        label: "Dead Air",
    },
    {
        id: "interruption",
        label: "Interruption",
    },
    {
        id: "rate_of_speech",
        label: "Rate of speech",
    },
    {
        id: "blank_call",
        label: "Blank Call",
    },
    {
        id: "owner_talk_ratio",
        label: "Agent Talk Ratio",
    },
    {
        id: "duration",
        label: "Talk Time",
    },
    {
        id: "account_index",
        label: "index",
    },
];

export const participantOption = [
    {
        id: "anyone",
        label: "Anyone",
    },
    {
        id: "client",
        label: "Client",
    },
    {
        id: "owner",
        label: "Rep",
    },
];

export type ReactFlowStateProviderProps = PropsWithChildren<{
    isGroupBlockTree?: boolean;
    state?: {
        nodes: Node[];
        edges: Edge[];
    };
}>;

export const nodeTypes: NodeTypes = {
    [BertNodeKeys.createSingleResponseNode]: CreateSingleResponseNode,
    [BertNodeKeys.createMultiResponseNode]: CreateMultipleResponseNode,
    [BertNodeKeys.singleBlockNode]: SingleBlockNode,
    [BertNodeKeys.conditionalNode]: MultiResponseNode,
    [BertNodeKeys.responseNode]: MultiResponseNode,
    [BertNodeKeys.combinationBlockNode]: CombinationBlockNode,
    [BertNodeKeys.groupBlockNode]: GroupBlockNode,
    [BertNodeKeys.conditionalGptNode]: MultiResponseNode,
};
export const edgeTypes: EdgeTypes = {
    [BertEdgeKeys.logicOperatorEdge]: LogicOperatorEdge,
    [BertEdgeKeys.customDeleteEdge]: CustomDeleteEdge,
};

export const singleNodeTypesMap: Record<
    SingleResponseNode<NonNullable<SingleResponseBlockType>>["type"],
    string
> = {
    attribute: "Attribute",
    moment: "Moment",
    function: "Function",
    parameter: "Parameter",
    validateInfo: "Validate Info",
    else: "Else",
};

export const operatorMap: Record<
    NonNullable<ValidateInfoBlockFormType["operator"]>,
    string
> = {
    lt: "Less Than",
    gt: "Greater Than",
    lte: "Less Than or Equal To",
    gte: "Greater Than or Equal To",
    between: "Between",
    same_day: "Same Day",
    same_month: "Same Month",
    same_year: "Same Year",
    equals: "Equals",
    isTrue: "Is True",
    isFalse: "Is False",
    contains: "Contains",
    not_contains: "Not Contains",
    starts_with: "Starts With",
    ends_with: "Ends With",
    not_equals: "Not Equals",
};
